"use client";

import React, { useState, useCallback, useMemo } from "react";
import { Plus, Mail, Briefcase } from "lucide-react";
import { Button } from "../../button";
import { useToast } from "../../../hooks/use-toast";
import { ResponsiveCommandSelect } from "../../responsive-select/ResponsiveCommandSelect";
import { SelectOption } from "../../responsive-select/types";

interface AttendeeSelectWithModalProps {
  currentEmails: string[];
  onChange: (emails: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  // Optional props for contact creation modal
  showCreateModal?: boolean;
  onCreateContact?: (email: string) => void;
  onModalClose?: () => void;
  // Optional props for external contact data
  contacts?: Contact[];
  onSearchContacts?: (searchTerm: string) => Promise<Contact[]>;
  isLoadingContacts?: boolean;
}

interface Contact {
  id: string;
  name: string;
  email: string;
  company_name?: string;
}

interface EnhancedAttendeeOption extends SelectOption {
  email?: string;
  company_name?: string;
}

// Custom option renderer for rich attendee details
const renderAttendeeOption = (option: SelectOption) => {
  const enhancedOption = option as EnhancedAttendeeOption;
  
  return (
    <div className="flex flex-col gap-1.5 w-full">
      {/* Header: Name */}
      <div className="flex items-center gap-2">
        <span className="font-medium">{enhancedOption.label}</span>
      </div>
      
      {/* Details: Company, Email in horizontal layout */}
      <div className="flex flex-wrap gap-3 text-xs text-muted-foreground">
        {enhancedOption.company_name && (
          <div className="flex items-center gap-1">
            <Briefcase className="h-3 w-3 flex-shrink-0" />
            <span>{enhancedOption.company_name}</span>
          </div>
        )}
        {enhancedOption.email && (
          <div className="flex items-center gap-1 min-w-0">
            <Mail className="h-3 w-3 flex-shrink-0" />
            <span className="truncate">{enhancedOption.email}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Mock contact data - in a real implementation, this would come from your API
const mockContacts: Contact[] = [
  { id: "1", name: "John Doe", email: "john@example.com", company_name: "Acme Corp" },
  { id: "2", name: "Jane Smith", email: "jane@example.com", company_name: "Tech Inc" },
  { id: "3", name: "Bob Johnson", email: "bob@example.com", company_name: "Startup LLC" },
  { id: "4", name: "Alice Brown", email: "alice@example.com", company_name: "Consulting Co" },
  { id: "5", name: "Charlie Wilson", email: "charlie@example.com", company_name: "Design Studio" },
];

export function AttendeeSelectWithModal({
  currentEmails,
  onChange,
  disabled = false,
  placeholder = "Search contacts or enter email addresses...",
  className,
  showCreateModal = false,
  onCreateContact,
  onModalClose,
  contacts,
  onSearchContacts,
  isLoadingContacts = false,
}: AttendeeSelectWithModalProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedContactId, setSelectedContactId] = useState<string>("");
  const { toast } = useToast();

  // Use provided contacts or fall back to mock data
  const availableContacts = contacts || mockContacts;

  // Handle contact search - simple synchronous function like ClientSelectWithModal
  const handleSearchChange = useCallback((newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  }, []);

  // Filter contacts based on search term - use provided contacts directly
  const filteredContacts = useMemo(() => {
    // If we have external contacts (from ClientAttendeeSelector), use them directly
    // The ClientAttendeeSelector handles the search logic via useContactsPaginated
    if (contacts) {
      return contacts;
    }
    
    // Fallback to local filtering for mock data
    if (!searchTerm || searchTerm.length < 2) return availableContacts;
    
    const search = searchTerm.toLowerCase();
    return availableContacts.filter(contact => 
      contact.name.toLowerCase().includes(search) ||
      contact.email.toLowerCase().includes(search) ||
      (contact.company_name && contact.company_name.toLowerCase().includes(search))
    );
  }, [searchTerm, availableContacts, contacts]);

  // Build enhanced options for the select component
  const buildOptions = useCallback((contacts: Contact[]) => {
    return contacts
      .filter(contact => !currentEmails.includes(contact.email)) // Don't show already selected
      .map(contact => ({
        value: contact.id,
        label: contact.name,
        description: contact.email,
        searchableText: `${contact.name} ${contact.email} ${contact.company_name || ""}`,
        email: contact.email, // Store email for easy access
        company_name: contact.company_name, // Store company for display
      }));
  }, [currentEmails]);

  const options = buildOptions(filteredContacts);

  const handleContactSelect = useCallback((contactId: string, option?: SelectOption) => {
    if (!option?.email) return;
    
    const email = option.email;
    if (!currentEmails.includes(email)) {
      const newEmails = [...currentEmails, email];
      onChange(newEmails);
      toast({
        title: "Attendee Added",
        description: `${option.label} has been added to the meeting.`,
      });
    }
    setSelectedContactId("");
    setSearchTerm("");
  }, [currentEmails, onChange, toast]);

  const handleManualEmailAdd = useCallback(() => {
    if (!searchTerm.trim()) return;
    
    // Check if it's a valid email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(searchTerm.trim())) {
      toast({
        title: "Invalid Email",
        description: "Please enter a valid email address.",
        variant: "destructive",
      });
      return;
    }

    if (!currentEmails.includes(searchTerm.trim())) {
      const newEmails = [...currentEmails, searchTerm.trim()];
      onChange(newEmails);
      toast({
        title: "Email Added",
        description: `${searchTerm.trim()} has been added to the meeting.`,
      });
    } else {
      toast({
        title: "Email Already Added",
        description: "This email is already in the attendee list.",
        variant: "destructive",
      });
    }
    
    setSearchTerm("");
  }, [searchTerm, currentEmails, onChange, toast]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // If there's a selected contact, add it; otherwise try to add as manual email
      if (selectedContactId) {
        const option = options.find(opt => opt.value === selectedContactId);
        if (option) {
          handleContactSelect(selectedContactId, option);
        }
      } else {
        handleManualEmailAdd();
      }
    }
  };

  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <div className="flex-1">
          <ResponsiveCommandSelect
            value={selectedContactId}
            onValueChange={handleContactSelect}
            options={options}
            placeholder={placeholder}
            disabled={disabled}
            searchValue={searchTerm}
            onSearchChange={handleSearchChange}
            isSearching={isLoadingContacts || (onSearchContacts && searchTerm.length >= 2)}
            className="w-full"
            title="Add Attendees"
            description="Search contacts or enter email addresses"
            searchPlaceholder="Search by name, email, or company..."
            enableRecents
            recentKey="attendees"
            recentMax={8}
            customOptionRenderer={renderAttendeeOption}
          />
        </div>
        <Button
          type="button"
          onClick={handleManualEmailAdd}
          disabled={disabled || !searchTerm.trim()}
          className="bg-blue-600 hover:bg-blue-700 text-white"
          title="Add email address"
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      
      {/* Instructions */}
      <div className="text-xs text-gray-600 space-y-1">
        <div>• Search and select from your contacts</div>
        <div>• Or type an email address and press Enter or click +</div>
        <div>• Press Enter to add the selected contact</div>
      </div>
    </div>
  );
}

export default AttendeeSelectWithModal;
